import {
    ArrowOutwardRounded as ArrowOutwardRoundedIcon,
    AttachMoney,
    Block as BlockIcon,
    BusinessCenter,
    GroupOutlined as GroupOutlinedIcon,
    InfoOutlined as InfoOutlinedIcon,
    Person,
} from 'components/icons';
import ExclamationMarkIcon from 'assets/images/icons/ExclamationMarkIcon.svg?react';
import Chip from 'components/@extended/Chip';
import SegmentChip from 'components/@extended/SegmentChip';
import Loader from 'components/Loader';
import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import DmsLink from 'components/dmslink/DmsLink';
import { MissingValue } from 'components/misc/MissingValue';
import PartyName from 'components/party/PartyName';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig, MainFilterForm } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Tooltip } from 'components/ui/tooltip';
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    useGetMarketingProfilesWithFiltersQuery,
    useLazyPreviewMarketingProfileContactsCountQuery,
    useLazyPreviewMarketingProfileContactsQuery,
} from 'store/api/marketingProfiles';
import { VerificationStatusType } from 'types/compliance';
import { ContactListItem, PartyToPartyRelationDto, PartyType, RelationshipTypeCode } from 'types/contacts';
import { MarketingProfileCreateUpdateRequest, MarketingProfileFilterCriteria } from 'types/marketingProfiles';
import { PaginationProps } from 'types/paginationAndFilter';
import { errorAlert } from 'utils/alert';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import {
    buildInitialSearchParams,
    extractFilterCriteriaFromMainFilterState,
    hasAnyCriteria,
    isSameCriteria,
    normalizeFilterCriteria,
} from './marketingProfileFormUtils';
import { MARKETING_PROFILE_CLASSIFIERS, useMarketingProfileFilters } from './useMarketingProfileFilters';
import { Setting } from 'types/back-office-settings';
import { useSettings } from 'utils/useSettings';

type Props = {
    initialValue?: MarketingProfileCreateUpdateRequest;
    /** The profile shown, left out of the profiles its filters can name; none for a new one. */
    profileId?: number;
    onStateChange: (
        state: Partial<{
            filterCriteria: MarketingProfileFilterCriteria;
            isDirty: boolean;
            hasCriteria: boolean;
            count?: number;
        }> | null
    ) => void;
};

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stubLazyQueryResult = <T,>(value: T): any => {
    const promise = Promise.resolve(value);
    return Object.assign(promise, {
        unwrap: () => Promise.resolve(value),
        refetch: () => stubLazyQueryResult(value),
        abort: () => {},
        unsubscribe: () => {},
        arg: undefined,
        requestId: '',
        subscriptionOptions: {},
        queryCacheKey: '',
        reset: () => {},
    });
};

const EMPTY_QUERY_RESPONSE = {
    content: [],
    totalElements: 0,
    totalPages: 0,
    last: true,
    size: 0,
    number: 0,
    sort: { empty: true, sorted: false, unsorted: true },
    first: true,
    numberOfElements: 0,
    pageable: {
        offset: 0,
        pageNumber: 0,
        pageSize: 0,
        paged: true,
        unpaged: false,
        sort: { empty: true, sorted: false, unsorted: true },
    },
    empty: true,
};

/**
 * The share of a column's least width it keeps in the create dialog, where the four of them, 780px on the
 * profile's page, have the dialog's 670px to fit in rather than a sideways scroll.
 */
const CREATE_DIALOG_MIN_WIDTH_SHARE = 0.85;

const getContactHref = (contact: ContactListItem) => `/contact/${contact.id}`;

const MarketingProfileContactsTable = ({ initialValue, profileId, onStateChange }: Props) => {
    const minWidthShare = useInCreateDialog() ? CREATE_DIALOG_MIN_WIDTH_SHARE : 1;
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: MARKETING_PROFILE_CLASSIFIERS });
    const isNonVerifiedIconEnabled = useSettings().isSettingEnabled(Setting.NON_VERIFIED_CLIENT_ICON);

    const [fetchData, { data, isFetching, error }] = useLazyPreviewMarketingProfileContactsQuery();

    const criteriaRef = useRef<MarketingProfileFilterCriteria>(
        initialValue?.filterCriteria ? normalizeFilterCriteria(initialValue.filterCriteria) : {}
    );

    // Every profile at once: a saved filter on one is read back against these options when the list mounts.
    // shortcut: only the first 1000 profiles are offered, so a saved reference past them is dropped on the next save;
    // page or search the options once a firm nears it.
    const { data: profiles, isLoading: isLoadingProfiles } = useGetMarketingProfilesWithFiltersQuery({
        pageSize: 1000,
    });
    const otherProfiles = useMemo(
        () =>
            (profiles?.content ?? [])
                .filter((profile) => profile.id !== profileId)
                .map((profile) => ({ value: profile.id, label: profile.name ?? '' })),
        [profiles, profileId]
    );
    const filterDefs = useMarketingProfileFilters(classifiers, otherProfiles);

    // Read by the list when it mounts and again when the filter definitions change: from the applied criteria, which
    // the fetch reads too, so a rebuild keeps what the user applied.
    const initialSearchParams = useMemo(
        () =>
            hasAnyCriteria(criteriaRef.current) ? buildInitialSearchParams(criteriaRef.current, filterDefs) : undefined,
        [filterDefs]
    );

    useEffect(() => {
        if (error) errorAlert(getApiErrorMessage(error) ?? t('contacts.error-loading-contacts-list'));
    }, [error]);

    useEffect(() => {
        const saved = initialValue?.filterCriteria;
        if (!saved) return;
        const filterCriteria = normalizeFilterCriteria(saved);
        criteriaRef.current = filterCriteria;
        onStateChange({ filterCriteria, isDirty: false, hasCriteria: hasAnyCriteria(filterCriteria) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue?.filterCriteria]);

    const handleApplyFilterState = useCallback(
        (state: MainFilterForm) => {
            const filterCriteria = extractFilterCriteriaFromMainFilterState(
                state,
                filterDefs,
                initialValue?.filterCriteria ?? {}
            );
            const isDirty = !isSameCriteria(filterCriteria, initialValue?.filterCriteria ?? {});
            criteriaRef.current = filterCriteria;
            onStateChange({ filterCriteria, isDirty, hasCriteria: hasAnyCriteria(filterCriteria) });
        },
        [initialValue?.filterCriteria, onStateChange, filterDefs]
    );

    const handleTotalCountChange = useCallback(
        (count: number | undefined) => {
            onStateChange({ count });
        },
        [onStateChange]
    );

    function getEmployers(original_cell: ContactListItem | undefined) {
        if (!original_cell?.partyType || !original_cell.relatesTo) return [];
        if (original_cell.partyType === 'COMPANY') {
            return original_cell.relatesTo.filter(
                (item: { partyRelationshipTypeCode: string }) => item.partyRelationshipTypeCode === 'PERSON'
            );
        } else if (original_cell.partyType === 'PERSON') {
            return original_cell.relatesTo.filter(
                (item: { partyRelationshipTypeCode: string }) => item.partyRelationshipTypeCode === 'EMPLOYEE'
            );
        }
        return [];
    }

    const columns = useMemo<DataTableColumnDef<ContactListItem>[]>(
        () => [
            {
                header: t('name'),
                accessorKey: 'name',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 200 * minWidthShare,
                },
                cell: ({ row }) => {
                    const contactListItem = row.original;
                    return (
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <PartyName
                                    party={contactListItem}
                                    isLink
                                    plainLink
                                    variant="list"
                                    showPartyNumber
                                    className="font-semibold"
                                />
                                {contactListItem.dmsLink && (
                                    <DmsLink link={contactListItem.dmsLink} isIconDisplayed={true} />
                                )}
                                {contactListItem.officialName &&
                                    contactListItem.officialName !== contactListItem.name && (
                                        <Tooltip content={contactListItem.officialName}>
                                            <span
                                                tabIndex={0}
                                                role="img"
                                                aria-label={contactListItem.officialName}
                                                className="inline-flex"
                                            >
                                                <InfoOutlinedIcon className="text-grey-600 h-[1.3rem]" />
                                            </span>
                                        </Tooltip>
                                    )}
                            </div>
                            <div className="flex gap-2">
                                <div className="flex gap-0.5">
                                    {isNonVerifiedIconEnabled &&
                                        contactListItem.relationshipTypeCode === RelationshipTypeCode.CLIENT &&
                                        contactListItem.complianceVerificationStatusTypeCode ===
                                            VerificationStatusType.NOT_VERIFIED && (
                                            <Tooltip content={t('compliance.not-verified')}>
                                                <div>
                                                    <Chip
                                                        size="small"
                                                        label={<ExclamationMarkIcon />}
                                                        type={'partyType'}
                                                        isError
                                                    />
                                                </div>
                                            </Tooltip>
                                        )}
                                    {contactListItem.isTimecardAddingAutoBlocked ? (
                                        <Tooltip content={t('dashboard.tooltips.timecard-blocked-short')}>
                                            <div>
                                                <Chip size="small" label={<BlockIcon />} type={'partyType'} isError />
                                            </div>
                                        </Tooltip>
                                    ) : null}
                                    {contactListItem.partyType === PartyType.COMPANY ? (
                                        <Tooltip content={t('contacts.types.company')}>
                                            <div>
                                                <Chip size="small" label={<BusinessCenter />} type={'partyType'} />
                                            </div>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip content={t('contacts.types.person')}>
                                            <div>
                                                <Chip size="small" label={<Person />} type={'partyType'} />
                                            </div>
                                        </Tooltip>
                                    )}
                                    {contactListItem.relationshipTypeCode === RelationshipTypeCode.CLIENT ? (
                                        <Tooltip
                                            content={getClassifierText(classifiers['RELATIONSHIP_TYPE'], 'CLIENT')}
                                        >
                                            <div>
                                                <Chip size="small" label={<AttachMoney />} type={'partyType'} />
                                            </div>
                                        </Tooltip>
                                    ) : null}
                                    {contactListItem.relationshipTypeCode === RelationshipTypeCode.LEAD ? (
                                        <Tooltip content={getClassifierText(classifiers['RELATIONSHIP_TYPE'], 'LEAD')}>
                                            <div>
                                                <Chip
                                                    size="small"
                                                    label={<ArrowOutwardRoundedIcon />}
                                                    type={'partyType'}
                                                />
                                            </div>
                                        </Tooltip>
                                    ) : null}
                                </div>
                                <div className="flex gap-0.5">
                                    {contactListItem.segmentTypeCode != null && (
                                        <SegmentChip segmentTypeCode={contactListItem.segmentTypeCode} />
                                    )}
                                    {contactListItem.serviceLevelTypeCode != null && (
                                        <span className="text-subtitle2 text-grey-600 flex items-center font-semibold">
                                            {getClassifierText(
                                                classifiers['SERVICE_LEVEL_TYPE'],
                                                contactListItem.serviceLevelTypeCode
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                },
            },
            {
                header: t('email'),
                accessorKey: 'mainEmail',
                enableSorting: false,
                meta: {
                    widthWeight: 1.31,
                    minPx: 240 * minWidthShare,
                },
                cell: ({ cell }) => {
                    const { mainEmail } = cell.row.original;
                    return mainEmail?.length ? (
                        <a className={LINK_CLASS} href={`mailto:${mainEmail}`}>
                            {mainEmail}
                        </a>
                    ) : (
                        <MissingValue />
                    );
                },
            },
            {
                header: t('contacts.mobile-phone'),
                accessorKey: 'mainMobile',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 160 * minWidthShare,
                },
                cell: ({ cell }) => {
                    const { mainMobile } = cell.row.original;
                    return mainMobile?.length ? <p className="text-body1">{mainMobile}</p> : <MissingValue />;
                },
            },
            {
                header: t('contacts.employed-by'),
                id: 'employed',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 180 * minWidthShare,
                },
                cell: ({ cell }) => {
                    const relatesTo = getEmployers(cell.row?.original);
                    if (relatesTo.length === 0) {
                        return <MissingValue />;
                    }
                    const links = relatesTo.map((relation: PartyToPartyRelationDto, index: number) => (
                        <Fragment key={`${relation.otherParty.id}-${relation.otherParty.name}`}>
                            <PartyName party={relation.otherParty} isLink variant="list" />
                            {index < relatesTo.length - 1 && <span>,&nbsp;</span>}
                        </Fragment>
                    ));
                    return <div>{links}</div>;
                },
            },
        ],
        [isNonVerifiedIconEnabled, classifiers, minWidthShare]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            searchBarSelectors: [
                { selector: 'name' },
                { selector: 'partyNumber' },
                { selector: 'mainEmail' },
                { selector: 'mainPhone', comparator: '=phonelk=' },
                { selector: 'mainMobile', comparator: '=phonelk=' },
                { selector: 'partners.employee.fullName' },
                { selector: 'managers.employee.fullName' },
            ],
            filters: filterDefs.map(({ filter }) => filter),
        }),
        [filterDefs]
    );

    const useFetchCount = useMemo(() => {
        const useAdaptedCount = (options?: Parameters<typeof useLazyPreviewMarketingProfileContactsCountQuery>[0]) => {
            const [trigger, state] = useLazyPreviewMarketingProfileContactsCountQuery(options);
            const wrapped = useCallback(
                (args: { filters?: string }, preferCacheValue?: boolean) => {
                    if (!hasAnyCriteria(criteriaRef.current)) {
                        return stubLazyQueryResult({ count: 0 });
                    }
                    return trigger({ ...args, criteria: criteriaRef.current }, preferCacheValue);
                },
                [trigger]
            );
            return [wrapped, state];
        };
        return useAdaptedCount as unknown as typeof useLazyPreviewMarketingProfileContactsCountQuery;
    }, []);

    const wrappedFetchData = useCallback(
        (paginationProps: PaginationProps) => {
            if (!hasAnyCriteria(criteriaRef.current)) {
                return stubLazyQueryResult(EMPTY_QUERY_RESPONSE);
            }
            return fetchData({ ...paginationProps, criteria: criteriaRef.current });
        },
        [fetchData]
    );

    if (isLoadingClassifiers || isLoadingProfiles) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<ContactListItem>
            getRowHref={getContactHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="marketing-profile-contacts"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="marketing-profiles.persons"
            singularTextId="marketing-profiles.person"
            countTooltip="marketing-profiles.tooltips.count"
            noEntriesId="marketing-profiles.no-persons-found"
            noEntriesBodyId="marketing-profiles.empty.contacts-body"
            noEntriesIcon={GroupOutlinedIcon}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            fetchData={wrappedFetchData as any}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            fetchCount={useFetchCount as any}
            filterConfig={filterConfig}
            rowsPerPage={50}
            canRememberFilters={false}
            hideClearFilters
            disableUrlState
            initialSearchParams={initialSearchParams}
            onApplyFilterState={handleApplyFilterState}
            onTotalCountChange={handleTotalCountChange}
        />
    );
};

export default MarketingProfileContactsTable;
